import { useContext, useEffect, useRef, useState } from "react";
import useAsyncEffect from "use-async-effect";
import { img, imgTileFromCoords } from "../utils/settings";
import { fetchHexImage } from "../World/hexImageLoader";
import cardBg from "./card-image-bg.png";
import cardShadow from "./card-shadow.png";
import { AppContext } from "../State/Context";
import { ActionType } from "../State/State";

function parseMtimeFromHeaders(headers: Headers): number {
  let epochSec = 0;
  const mtimeHeader = headers.get("x-amz-meta-mtime");
  if (mtimeHeader) {
    const n = Number(mtimeHeader.trim());
    if (!isNaN(n)) epochSec = Math.floor(n);
  }
  if (epochSec === 0) {
    const lm = headers.get("last-modified");
    if (lm) {
      const t = lm.trim();
      if (/^\d+$/.test(t)) {
        const n = Number(t);
        if (!isNaN(n)) epochSec = n;
      } else {
        const d = Date.parse(t);
        if (!isNaN(d)) epochSec = d / 1000;
      }
    }
  }
  return Math.floor(epochSec);
}

const RECENTLY_GENERATED_MS = 15 * 60 * 1000;

type CardImageProps = {
  x: number;
  y: number;
  offset?: { x: number; y: number };
  hideShadow?: boolean;
  /** If set and image was generated less than 15 min ago, add ?t= and retry on 404 */
  imageGeneratedAt?: number;
  /** Cache-bust version for center tile (e.g. cardVersion after regen) */
  imageVersion?: number;
};

const EPOCH_OFFSET = 1700000000;
const IMAGE_RETRY_INTERVAL_MS = 5555;
const MAX_IMAGE_RETRIES = 12;

const HEX_POSITIONS: Record<string, string> = {
  main: "absolute w-[80%] aspect-[1/1]",
  topLeft: "absolute w-[80%] aspect-[1/1] top-[-6.7%] left-[-23%]",
  topRight: "absolute w-[80%] aspect-[1/1] top-[-6.7%] right-[-22.9%]",
  left: "absolute w-[80%] aspect-[1/1] top-[23.3%] left-[-55.9%]",
  right: "absolute w-[80%] aspect-[1/1] top-[23.3%] right-[-56%]",
  bottomLeft: "absolute w-[80%] aspect-[1/1] bottom-[-6.7%] left-[-22.9%]",
  bottomRight: "absolute w-[80%] aspect-[1/1] bottom-[-6.7%] right-[-22.9%]",
};

function tileKey(cx: number, cy: number) {
  return `${cx}-${cy}`;
}

const NEIGHBOR_ORDER: (keyof Neighbors)[] = [
  "topLeft",
  "topRight",
  "left",
  "right",
  "bottomLeft",
  "bottomRight",
];

export const CardImage: React.FC<CardImageProps> = ({
  x,
  y,
  offset = { x: 0, y: 0 },
  hideShadow,
  imageGeneratedAt,
  imageVersion,
}) => {
  const { state, dispatch } = useContext(AppContext);
  const neighbors = getHexagonalNeighbors(x, y);
  const [blobURLByKey, setBlobURLByKey] = useState<Record<string, string>>({});
  const blobURLsToRevokeRef = useRef<string[]>([]);
  const [hiddenNeighbors, setHiddenNeighbors] = useState<Record<string, boolean>>({});
  const tileZ = state.tileZIndexes;
  const openTimeRef = useRef<number>(Date.now());
  const isRecentlyGenerated =
    imageGeneratedAt != null && Date.now() - imageGeneratedAt < RECENTLY_GENERATED_MS;
  useEffect(() => {
    openTimeRef.current = Date.now();
  }, [x, y]);

  useEffect(() => {
    return () => {
      blobURLsToRevokeRef.current.forEach((url) => URL.revokeObjectURL(url));
      blobURLsToRevokeRef.current = [];
    };
  }, []);

  const hideNeighbor = (key: string) => {
    setHiddenNeighbors((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  const shadowOffset = {
    x: offset.x / 15,
    y: offset.y / 5,
  };

  // Only fetch blobs for display; z-index comes from state.tileZIndexes (already set by map / prior load).
  useAsyncEffect(async () => {
    const cache = state.tileZIndexes;
    const neighborEntries = Object.entries(neighbors) as [string, { x: number; y: number }][];
    const mainKey = tileKey(x, y);
    const keysToFetch = [mainKey, ...neighborEntries.map(([, c]) => tileKey(c.x, c.y))].filter(
      (k) => !(k in cache)
    );
    if (keysToFetch.length === 0) return;

    const loadTile = async (
      nx: number,
      ny: number,
      isMain: boolean
    ): Promise<string | null> => {
      const key = tileKey(nx, ny);
      if (key in cache) return null;
      const centerVersion = nx === x && ny === y ? imageVersion : undefined;
      const baseUrl = imgTileFromCoords(nx, ny, state.highQuality, centerVersion);
      const useCacheBuster = isMain && isRecentlyGenerated;
      const tryFetch = (): Promise<string | null> =>
        fetchHexImage(baseUrl + (useCacheBuster ? "?t=" + Date.now() : ""))
          .then(async (res) => {
            if (!res.ok) return null;
            const epochSec = parseMtimeFromHeaders(res.headers);
            const z = epochSec - EPOCH_OFFSET;
            dispatch({ type: ActionType.SET_TILE_Z_INDEX, x: nx, y: ny, zIndex: z });
            const blob = await res.blob();
            const objectURL = URL.createObjectURL(blob);
            blobURLsToRevokeRef.current.push(objectURL);
            return objectURL;
          })
          .catch(() => null);

      if (!isMain || !isRecentlyGenerated) {
        const url = await tryFetch();
        return url;
      }
      for (let attempt = 0; attempt < MAX_IMAGE_RETRIES; attempt++) {
        const url = await tryFetch();
        if (url) return url;
        if (attempt < MAX_IMAGE_RETRIES - 1) {
          await new Promise((r) => setTimeout(r, IMAGE_RETRY_INTERVAL_MS));
        }
      }
      return null;
    };

    const newBlobURLs: Record<string, string> = {};
    const mainBlob = await loadTile(x, y, true);
    if (mainBlob) newBlobURLs[tileKey(x, y)] = mainBlob;
    await Promise.all(
      neighborEntries.map(async ([, coord]) => {
        const url = await loadTile(coord.x, coord.y, false);
        if (url) newBlobURLs[tileKey(coord.x, coord.y)] = url;
      })
    );
    setBlobURLByKey((prev) => {
      const toRevoke: string[] = [];
      Object.keys(newBlobURLs).forEach((k) => {
        if (prev[k] && prev[k] !== newBlobURLs[k]) toRevoke.push(prev[k]);
      });
      toRevoke.forEach((url) => URL.revokeObjectURL(url));
      blobURLsToRevokeRef.current = blobURLsToRevokeRef.current.filter((u) => !toRevoke.includes(u));
      return { ...prev, ...newBlobURLs };
    });
  }, [x, y, imageGeneratedAt]);

  const prevHighQualityRef = useRef(state.highQuality);
  useEffect(() => {
    if (prevHighQualityRef.current === state.highQuality) return;
    prevHighQualityRef.current = state.highQuality;
    const keysToClear = [
      tileKey(x, y),
      ...NEIGHBOR_ORDER.map((key) => neighbors[key])
        .filter((c): c is { x: number; y: number } => c != null)
        .map((c) => tileKey(c.x, c.y)),
    ];
    setBlobURLByKey((prev) => {
      const toRevoke = keysToClear.map((k) => prev[k]).filter(Boolean) as string[];
      toRevoke.forEach((url) => URL.revokeObjectURL(url));
      blobURLsToRevokeRef.current = blobURLsToRevokeRef.current.filter((u) => !toRevoke.includes(u));
      const next = { ...prev };
      keysToClear.forEach((k) => delete next[k]);
      return next;
    });
  }, [state.highQuality, x, y]);

  const tiles: { key: string; cx: number; cy: number; zIndex: number }[] = [];
  tiles.push({ key: "main", cx: x, cy: y, zIndex: tileZ[tileKey(x, y)] ?? 0 });
  NEIGHBOR_ORDER.forEach((key) => {
    const coord = neighbors[key];
    if (coord && !hiddenNeighbors[key])
      tiles.push({
        key,
        cx: coord.x,
        cy: coord.y,
        zIndex: tileZ[tileKey(coord.x, coord.y)] ?? 0,
      });
  });

  return (
    <div
      className="relative w-full aspect-[2/3] flex items-center justify-center p-4 pointer-events-none"
      style={{
        backgroundImage: `url(${cardBg})`,
        backgroundSize: "fill",
        backgroundPosition: "center",
      }}
    >
      {tiles.map(({ key, cx, cy, zIndex }) => {
        const base = imgTileFromCoords(
          cx,
          cy,
          state.highQuality,
          cx === x && cy === y ? imageVersion : undefined
        );
        const fallbackSrc =
          key === "main" && isRecentlyGenerated
            ? base + "?t=" + openTimeRef.current
            : base;
        return (
          <img
            key={key}
            src={blobURLByKey[tileKey(cx, cy)] || fallbackSrc}
            alt=""
            className={HEX_POSITIONS[key] ?? "absolute w-[80%] aspect-[1/1]"}
            style={{ zIndex }}
            onError={key === "main" ? undefined : () => hideNeighbor(key)}
          />
        );
      })}

      {!hideShadow && (
        <img
          src={cardShadow}
          alt="Card Shadow"
          className="absolute opacity-90 top-[-10%] left-[-1%] w-[120%] h-[120%] z-[1000000000] transition-transform duration-100 ease-out"
          style={{
            transform: `translate(${shadowOffset.x}%, ${shadowOffset.y}%)`,
          }}
        />
      )}
    </div>
  );
};

interface Coordinate {
  x: number;
  y: number;
}

interface Neighbors {
  topLeft?: Coordinate;
  topRight?: Coordinate;
  left?: Coordinate;
  right?: Coordinate;
  bottomLeft?: Coordinate;
  bottomRight?: Coordinate;
}

function getHexagonalNeighbors(x: number, y: number): Neighbors {
  const neighbors: Neighbors = {};
  const evenRowGap = y % 2 === 0 ? 1 : 0;
  neighbors.topLeft = { x: x - evenRowGap, y: y - 1 };
  neighbors.topRight = { x: x + (1 - evenRowGap), y: y - 1 };
  neighbors.left = { x: x - 1, y };
  neighbors.right = { x: x + 1, y };
  neighbors.bottomLeft = { x: x - evenRowGap, y: y + 1 };
  neighbors.bottomRight = { x: x + (1 - evenRowGap), y: y + 1 };
  return neighbors;
}
